import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare, X, Send, Loader2, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';
import Markdown from 'react-markdown';

type Role = 'user' | 'model';

interface Message {
  id: string;
  role: Role;
  text: string;
  isError?: boolean;
}

export function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'model',
      text: 'Hello! I am the FIVE LENS Assistant. How can I help you today?',
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = { id: Date.now().toString(), role: 'user', text: messageText.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const modelMessageId = (Date.now() + 1).toString();
    setMessages((prev) => [...prev, { id: modelMessageId, role: 'model', text: '' }]);

    try {
      // Build the history for the API, excluding welcome message
      const currentHistory = messages
        .filter(m => m.id !== 'welcome' && !m.isError && m.text.trim())
        .map(m => ({
          role: m.role,
          parts: [{ text: m.text }]
        }));
      
      // Add the new user message
      currentHistory.push({
        role: 'user',
        parts: [{ text: userMessage.text }]
      });

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ history: currentHistory })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch chat response');
      }

      if (!response.body) throw new Error('ReadableStream not supported');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          const chunkValue = decoder.decode(value, { stream: true });
          const lines = chunkValue.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                done = true;
                break;
              }
              try {
                const parsed = JSON.parse(data);
                if (parsed.error) throw new Error(parsed.error);
                if (parsed.text) {
                  setMessages((prev) => 
                    prev.map(m => 
                      m.id === modelMessageId 
                        ? { ...m, text: m.text + parsed.text }
                        : m
                    )
                  );
                }
              } catch (e) {
                console.error('Error parsing stream data:', e);
              }
            }
          }
        }
      }
    } catch (error: any) {
      console.error(error);
      setMessages((prev) => 
        prev.map(m => 
          m.id === modelMessageId 
            ? { ...m, text: error.message || 'Sorry, I encountered an error.', isError: true }
            : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating Action Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: isOpen ? 0 : 1 }}
        transition={{ duration: 0.2 }}
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 lg:bottom-10 lg:right-10 w-14 h-14 rounded-full bg-rule-3 text-white shadow-lg flex items-center justify-center hover:bg-rule-3 transition-colors z-[90]",
          isOpen ? "pointer-events-none" : ""
        )}
        aria-label="Open Chat"
      >
        <MessageSquare className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 lg:bottom-10 lg:right-10 w-[380px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-[90] overflow-hidden border border-ground-deep"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-ink text-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-rule-3 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-serif font-medium text-lg leading-tight">FIVE LENS Assistant</h3>
                  <p className="text-[10px] text-white/70 tracking-widest uppercase font-bold">Ask anything</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                aria-label="Close Chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-ground-alt/50">
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={cn(
                    "flex w-full",
                    msg.role === 'user' ? "justify-end" : "justify-start"
                  )}
                >
                  <div 
                    className={cn(
                      "max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm",
                      msg.role === 'user' 
                        ? "bg-rule-3 text-white rounded-tr-sm" 
                        : "bg-white border border-ground-deep text-ink rounded-tl-sm",
                      msg.isError && "border-rule-3/50 text-rule-3 bg-rule-3/5"
                    )}
                  >
                    {msg.role === 'model' ? (
                      <div className={cn(
                        "prose prose-sm max-w-none prose-p:leading-relaxed",
                        "prose-a:text-rule-3 hover:prose-a:text-ink",
                        msg.isError && "prose-p:text-rule-3"
                      )}>
                        {msg.text ? (
                          <Markdown>{msg.text}</Markdown>
                        ) : (
                          <div className="flex items-center h-5">
                            <span className="w-1.5 h-1.5 bg-rule-3 rounded-full animate-bounce mr-1" />
                            <span className="w-1.5 h-1.5 bg-rule-3 rounded-full animate-bounce mr-1 delay-75" />
                            <span className="w-1.5 h-1.5 bg-rule-3 rounded-full animate-bounce delay-150" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                    )}
                  </div>
                </div>
              ))}
              {messages.length === 1 && !isLoading && (
                <div className="pt-2 pb-1">
                  <p className="text-[11px] font-bold text-ink/50 uppercase tracking-wider mb-2">Common questions:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "What are the 5 Lenses?",
                      "Practitioner Certification & ICF points",
                      "How do I register for a cohort?",
                      "Pricing for certifications & reports",
                    ].map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => sendMessage(suggestion)}
                        className="text-left text-xs bg-white border border-ground-deep hover:border-rule-3 hover:text-rule-3 text-ink/80 px-2.5 py-1.5 rounded-lg shadow-2xs transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-ground-deep">
              <form onSubmit={handleSubmit} className="relative flex items-center">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask a question..."
                  className="w-full bg-ground-alt border border-ground-deep rounded-full pl-5 pr-12 py-3 text-sm focus:outline-none focus:border-rule-3 focus:ring-1 focus:ring-rule-3 transition-colors"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="absolute right-2 w-8 h-8 rounded-full bg-rule-3 text-white flex items-center justify-center hover:bg-rule-3 disabled:bg-ground-deep disabled:text-ink/70/50 transition-colors"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 ml-0.5" />}
                </button>
              </form>
              <div className="mt-3 text-center">
                <span className="text-[10px] text-ink/70/50 font-medium">Powered by Gemini AI</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
